import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Select, Button, InlineStack, Checkbox, Text, Badge, Spinner, BlockStack, TextField, Combobox, Listbox } from '@shopify/polaris';
import { getRequest } from '../../../../shared/components/functions.jsx';
import { useLookupStore } from '../../../../context/useStore';
import IntroductionWriter from './IntroductionWriter.jsx';

const DEFAULT_CLOSING_INTRO = 'Quotation according to our attached general terms and conditions of sale and delivery. All prices are EXW Dendermonde / Belgium and excluding VAT and other taxes.';

const DEFAULT_DESIGN_SCOPE_TEXT = `Design data used for this quotation:
- Bulk density of green coffee beans: 660 kg/m³
- Bulk density of roasted coffee beans: 330 kg/m³
- Bulk density of ground coffee: 350 kg/m³
- Design complies with CE directives.
- Sound insulation is not included but can be added as an option.
- Based on RAL colours to be specified with the order.

Quotation according to our attached general terms and conditions of sale and delivery. All prices are EXW Dendermonde / Belgium and excluding VAT and other taxes.

Not included in this offer/to be provided by the customer:
- A proper network with internet connection should be provided and maintained by the customer.
- PC or tablet in the same network as the Roasthubs server to operate the installation, even when no internet connection is available.
- Individual machine controls for the available equipment (coffee grinders, etc.).

Not included in the offer:
- Installation and commissioning
- Electric cabling
- Transportation
- Packaging`;

const DEFAULT_ADDITIONAL_SERVICES_TEXT = `Additionally, we offer packaging (where required), shipping, assembly, commissioning, and training for a fixed price of EUR XXXXX,XXXX.00.

Included in this fixed price:
- Packaging (where required)
- Transportation and insurance
- Mechanical assembly of the complete system
- Electrical cabling and cable tray installation throughout the plant
- Water and compressed air connections
- Installation of detectors and sensors
- Commissioning
- Operator training (3 days, conducted concurrently with commissioning)
- Engineering and project coordination


Not included in the fixed price:
- Modifications to the building like roof perforations for chimneys.
- Bringing utilities up to the machines: water, gas, electricity, compressed air and internet.
- Chimneys and channels to the outside
- Heavy lifting tools like cranes, forklifts, elevators,..
- Provide the required installation space.
- Obtaining the required permits and approvals
- Emission measurements, noise measurements, and any other measurements not explicitly specified in this quotation.
- Any costs resulting from waiting times or delays for which Schuilenburg is not responsible will be charged additionally in accordance with the technician rates and conditions attached hereto.`;

const DEFAULT_COMMERCIAL_TERMS = Object.freeze({
  paymentLines: [
    '30% when ordering.',
    '60% when the equipment is ready for shipment - before delivery',
    '10% 30 days after commissioning or 60 days after delivery if commissioning is delayed for reasons beyond Schuilenburg NV responsibility.'
  ],
  warranty: '12 months on parts',
  deliveryTime: '6 months after advance received',
  laborCosts: 'Installation according to VT2026. Commissioning according to IT2026',
  quotationValidity: '4 weeks',
  conditionsTitle: 'Conditions',
  conditionsText: DEFAULT_CLOSING_INTRO,
  designScopeTitle: 'Design Basis, Scope and Exclusions',
  designScopeText: DEFAULT_DESIGN_SCOPE_TEXT,
  additionalServicesEnabled: false,
  additionalServicesTitle: 'Additional services at a fixed price',
  additionalServicesText: DEFAULT_ADDITIONAL_SERVICES_TEXT,
  closingIntro: '',
  closingFollowUp: 'We remain at your disposal for all your further questions.',
  signatureBlock: 'Kind regards,\nJan Adriaens\nCEO',
  attachmentsTitle: 'Attachment:',
  attachmentLines: ['General Conditions', 'General terms for technicians VT2026', 'General terms for technicians IT2026']
});

const DEFAULT_COMMERCIAL_TERMS_NL = Object.freeze({
  paymentLines: [
    '30% bij bestelling.',
    '60% wanneer de apparatuur klaar is voor verzending - vóór levering',
    '10% 30 dagen na inbedrijfstelling of 60 dagen na levering indien de inbedrijfstelling vertraging oploopt om redenen buiten de verantwoordelijkheid van Schuilenburg NV.'
  ],
  warranty: '12 maanden op onderdelen',
  deliveryTime: '6 maanden na ontvangst voorschot',
  laborCosts: 'Installatie volgens VT2026. Inbedrijfstelling volgens IT2026',
  quotationValidity: '4 weken',
  conditionsTitle: 'Voorwaarden',
  conditionsText: 'Offerte volgens onze bijgevoegde algemene verkoops- en leveringsvoorwaarden. Alle prijzen zijn EXW Dendermonde / België en exclusief btw en andere taksen.',
  designScopeTitle: 'Ontwerpbasis, omvang en uitsluitingen',
  designScopeText: `Ontwerpgegevens gebruikt voor deze offerte:
- Stortgewicht van groene koffiebonen: 660 kg/m³
- Stortgewicht van gebrande koffiebonen: 330 kg/m³
- Stortgewicht van gemalen koffie: 350 kg/m³
- Het ontwerp voldoet aan de CE-richtlijnen.
- Geluidsisolatie is niet inbegrepen maar kan als optie worden toegevoegd.
- Gebaseerd op RAL-kleuren te bepalen bij de bestelling.

Offerte volgens onze bijgevoegde algemene verkoops- en leveringsvoorwaarden. Alle prijzen zijn EXW Dendermonde / België en exclusief btw en andere taksen.

Niet inbegrepen in deze offerte/te voorzien door de klant:
- Een degelijk netwerk met internetverbinding moet door de klant worden voorzien en onderhouden.
- Pc of tablet in hetzelfde netwerk als de Roasthubs-server om de installatie te bedienen, ook wanneer er geen internetverbinding beschikbaar is.
- Individuele machinesturingen voor de aanwezige apparatuur (koffiemolens, enz.).

Niet inbegrepen in de offerte:
- Installatie en inbedrijfstelling
- Elektrische bekabeling
- Transport
- Verpakking`,
  additionalServicesEnabled: false,
  additionalServicesTitle: 'Bijkomende diensten tegen een vaste prijs',
  additionalServicesText: `Daarnaast bieden wij verpakking (waar nodig), transport, montage, inbedrijfstelling en opleiding aan voor een vaste prijs van EUR XXXXX,XXXX.00.

Inbegrepen in deze vaste prijs:
- Verpakking (waar nodig)
- Transport en verzekering
- Mechanische montage van de volledige installatie
- Elektrische bekabeling en installatie van kabelgoten doorheen de volledige site
- Water- en persluchtaansluitingen
- Installatie van detectoren en sensoren
- Inbedrijfstelling
- Opleiding van de operatoren (3 dagen, gelijktijdig met de inbedrijfstelling)
- Engineering en projectcoördinatie


Niet inbegrepen in de vaste prijs:
- Aanpassingen aan het gebouw zoals dakdoorvoeren voor schouwen.
- Het aanleveren van nutsvoorzieningen tot aan de machines: water, gas, elektriciteit, perslucht en internet.
- Schouwen en kanalen naar buiten
- Zware hijsmiddelen zoals kranen, heftrucks, liften,..
- Het voorzien van de vereiste installatieruimte.
- Het verkrijgen van de vereiste vergunningen en goedkeuringen
- Emissiemetingen, geluidsmetingen en alle andere metingen die niet expliciet in deze offerte zijn opgenomen.
- Alle kosten die voortvloeien uit wachttijden of vertragingen waarvoor Schuilenburg niet verantwoordelijk is, worden bijkomend aangerekend volgens de bijgevoegde tarieven en voorwaarden voor techniekers.`,
  closingIntro: '',
  closingFollowUp: 'Wij blijven ter beschikking voor al uw verdere vragen.',
  signatureBlock: 'Met vriendelijke groeten,\nJan Adriaens\nCEO',
  attachmentsTitle: 'Bijlage:',
  attachmentLines: ['Algemene voorwaarden', 'Algemene voorwaarden voor techniekers VT2026', 'Algemene voorwaarden voor techniekers IT2026']
});

const DEFAULT_COMMERCIAL_TERMS_FR = Object.freeze({
  paymentLines: [
    '30% à la commande.',
    "60% lorsque l'équipement est prêt pour l'expédition - avant la livraison",
    '10% 30 jours après la mise en service ou 60 jours après la livraison si la mise en service est retardée pour des raisons indépendantes de la responsabilité de Schuilenburg NV.'
  ],
  warranty: '12 mois sur les pièces',
  deliveryTime: "6 mois après réception de l'acompte",
  laborCosts: 'Installation selon VT2026. Mise en service selon IT2026',
  quotationValidity: '4 semaines',
  conditionsTitle: 'Conditions',
  conditionsText: "Devis selon nos conditions générales de vente et de livraison ci-jointes. Tous les prix s'entendent EXW Dendermonde / Belgique, hors TVA et autres taxes.",
  designScopeTitle: 'Base de conception, étendue et exclusions',
  designScopeText: `Données de conception utilisées pour ce devis :
- Densité apparente des grains de café vert : 660 kg/m³
- Densité apparente des grains de café torréfié : 330 kg/m³
- Densité apparente du café moulu : 350 kg/m³
- La conception est conforme aux directives CE.
- L'isolation acoustique n'est pas incluse mais peut être ajoutée en option.
- Basé sur des couleurs RAL à préciser à la commande.

Devis selon nos conditions générales de vente et de livraison ci-jointes. Tous les prix s'entendent EXW Dendermonde / Belgique, hors TVA et autres taxes.

Non inclus dans ce devis/à fournir par le client :
- Un réseau adéquat avec connexion internet doit être fourni et entretenu par le client.
- PC ou tablette dans le même réseau que le serveur Roasthubs pour piloter l'installation, même sans connexion internet.
- Commandes individuelles des machines pour l'équipement présent (moulins à café, etc.).

Non inclus dans le devis :
- Installation et mise en service
- Câblage électrique
- Transport
- Emballage`,
  additionalServicesEnabled: false,
  additionalServicesTitle: 'Services supplémentaires à prix fixe',
  additionalServicesText: `En complément, nous proposons l'emballage (si nécessaire), le transport, le montage, la mise en service et la formation pour un prix fixe de EUR XXXXX,XXXX.00.

Inclus dans ce prix fixe :
- Emballage (si nécessaire)
- Transport et assurance
- Montage mécanique de l'installation complète
- Câblage électrique et installation de chemins de câbles dans toute l'usine
- Raccordements en eau et en air comprimé
- Installation des détecteurs et capteurs
- Mise en service
- Formation des opérateurs (3 jours, en parallèle avec la mise en service)
- Ingénierie et coordination de projet


Non inclus dans le prix fixe :
- Modifications du bâtiment telles que les percements de toiture pour les cheminées.
- L'amenée des utilités jusqu'aux machines : eau, gaz, électricité, air comprimé et internet.
- Cheminées et conduits vers l'extérieur
- Moyens de levage lourds tels que grues, chariots élévateurs, ascenseurs,..
- La mise à disposition de l'espace d'installation requis.
- L'obtention des permis et autorisations nécessaires
- Mesures d'émissions, mesures de bruit et toutes autres mesures non explicitement prévues dans ce devis.
- Tous les frais résultant de temps d'attente ou de retards dont Schuilenburg n'est pas responsable seront facturés en supplément conformément aux tarifs et conditions des techniciens ci-joints.`,
  closingIntro: '',
  closingFollowUp: 'Nous restons à votre disposition pour toute question complémentaire.',
  signatureBlock: 'Cordialement,\nJan Adriaens\nCEO',
  attachmentsTitle: 'Annexe :',
  attachmentLines: ['Conditions générales', 'Conditions générales pour techniciens VT2026', 'Conditions générales pour techniciens IT2026']
});

const COMMERCIAL_TERMS_BY_LANGUAGE = Object.freeze({
  E: DEFAULT_COMMERCIAL_TERMS,
  N: DEFAULT_COMMERCIAL_TERMS_NL,
  F: DEFAULT_COMMERCIAL_TERMS_FR
});

function getDefaultCommercialTerms(language) {
  const normalized = String(language || 'E')
    .trim()
    .toUpperCase();
  return COMMERCIAL_TERMS_BY_LANGUAGE[normalized] || DEFAULT_COMMERCIAL_TERMS;
}

function parseCommercialTerms(input) {
  if (!input) return {};
  if (typeof input === 'string') {
    try {
      return JSON.parse(input);
    } catch (err) {
      return {};
    }
  }
  if (typeof input === 'object' && !Array.isArray(input)) return input;
  return {};
}

function normalizeBoolean(value, fallback = false) {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return value === true || value === 1;
}

function normalizeCommercialTerms(input, language = 'E') {
  const raw = parseCommercialTerms(input);
  const defaults = getDefaultCommercialTerms(language);
  const hasAttachmentLines = Object.prototype.hasOwnProperty.call(raw, 'attachmentLines');
  const hasDesignScopeText = Object.prototype.hasOwnProperty.call(raw, 'designScopeText');
  const rawPaymentLines = Array.isArray(raw.paymentLines) ? raw.paymentLines : [];
  const rawAttachmentLines = Array.isArray(raw.attachmentLines) ? raw.attachmentLines : typeof raw.attachmentLines === 'string' ? raw.attachmentLines.split(/\r?\n/) : [];
  const shouldClearMigratedClosingIntro = !hasDesignScopeText && String(raw.closingIntro ?? '').trim() === DEFAULT_CLOSING_INTRO;
  return {
    paymentLines: defaults.paymentLines.map((line, idx) => String(rawPaymentLines[idx] ?? line)),
    warranty: String(raw.warranty ?? defaults.warranty),
    deliveryTime: String(raw.deliveryTime ?? defaults.deliveryTime),
    laborCosts: String(raw.laborCosts ?? defaults.laborCosts),
    quotationValidity: String(raw.quotationValidity ?? defaults.quotationValidity),
    conditionsTitle: String(raw.conditionsTitle ?? defaults.conditionsTitle),
    conditionsText: String(raw.conditionsText ?? defaults.conditionsText),
    designScopeTitle: String(raw.designScopeTitle ?? defaults.designScopeTitle),
    designScopeText: String(raw.designScopeText ?? defaults.designScopeText),
    additionalServicesEnabled: normalizeBoolean(raw.additionalServicesEnabled, defaults.additionalServicesEnabled),
    additionalServicesTitle: String(raw.additionalServicesTitle ?? defaults.additionalServicesTitle),
    additionalServicesText: String(raw.additionalServicesText ?? defaults.additionalServicesText),
    closingIntro: String(shouldClearMigratedClosingIntro ? defaults.closingIntro : raw.closingIntro ?? defaults.closingIntro),
    closingFollowUp: String(raw.closingFollowUp ?? defaults.closingFollowUp),
    signatureBlock: String(raw.signatureBlock ?? defaults.signatureBlock),
    attachmentsTitle: String(raw.attachmentsTitle ?? defaults.attachmentsTitle),
    attachmentLines: (hasAttachmentLines ? rawAttachmentLines : defaults.attachmentLines).map((line) => String(line))
  };
}

function termsEqual(a, b, language = 'E') {
  return JSON.stringify(normalizeCommercialTerms(a, language)) === JSON.stringify(normalizeCommercialTerms(b, language));
}

// Strip markup and collapse whitespace so a value that round-tripped through the
// rich text editor still matches its untouched plain-text default.
function plainTextForCompare(value) {
  return String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

const TRANSLATABLE_TERM_FIELDS = [
  'warranty',
  'deliveryTime',
  'laborCosts',
  'quotationValidity',
  'conditionsTitle',
  'conditionsText',
  'designScopeTitle',
  'designScopeText',
  'additionalServicesTitle',
  'additionalServicesText',
  'closingIntro',
  'closingFollowUp',
  'signatureBlock',
  'attachmentsTitle'
];

// When the PDF language changes, swap every field that still holds a default
// text (in any language) for the new language's default. Custom text is kept.
function translateTermsForLanguage(currentTerms, nextLanguage) {
  const languages = Object.keys(COMMERCIAL_TERMS_BY_LANGUAGE);
  const nextDefaults = getDefaultCommercialTerms(nextLanguage);
  const next = { ...currentTerms };

  const matchesAnyDefault = (getFieldValue, value) => languages.some((lang) => plainTextForCompare(getFieldValue(COMMERCIAL_TERMS_BY_LANGUAGE[lang])) === plainTextForCompare(value));

  for (const field of TRANSLATABLE_TERM_FIELDS) {
    if (matchesAnyDefault((defaults) => defaults[field], currentTerms[field])) {
      next[field] = nextDefaults[field];
    }
  }

  next.paymentLines = (currentTerms.paymentLines || []).map((line, idx) => (matchesAnyDefault((defaults) => defaults.paymentLines[idx], line) ? nextDefaults.paymentLines[idx] : line));

  const joinLines = (attachmentLines) => (Array.isArray(attachmentLines) ? attachmentLines : []).map((line) => plainTextForCompare(line)).join('\n');
  if (languages.some((lang) => joinLines(COMMERCIAL_TERMS_BY_LANGUAGE[lang].attachmentLines) === joinLines(currentTerms.attachmentLines))) {
    next.attachmentLines = [...nextDefaults.attachmentLines];
  }

  return next;
}

function isInactiveUser(user) {
  const value = user?.Inactief;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  }
  return value === 1 || value === true;
}

const SNAPSHOT_EDITOR_TABS = [
  { id: 'introduction', content: 'Introduction' },
  { id: 'commercial-terms', content: 'Commercial terms' },
  { id: 'closing-block', content: 'Closing block' }
];

function hasHtmlMarkup(value) {
  return /<\/?[a-z][\s\S]*>/i.test(String(value || ''));
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function valueToEditorHtml(value) {
  const safe = String(value || '');
  const html = hasHtmlMarkup(safe) ? safe : escapeHtml(safe).replace(/\r?\n/g, '<br>');
  // Empty paragraphs (blank lines) need a <br> to stay visible in the editor
  return html.replace(/<p><\/p>/g, '<p><br></p>');
}

function normalizeEditorHtml(value) {
  const container = document.createElement('div');
  container.innerHTML = String(value || '');
  const isBlockTag = (tag) => tag === 'div' || tag === 'p';

  const readNodeStyle = (node) => {
    if (!node || node.nodeType !== Node.ELEMENT_NODE) return { bold: false, italic: false, underline: false };
    const tag = node.tagName.toLowerCase();
    const fontWeight = String(node.style?.fontWeight || '').toLowerCase();
    const textDecoration = String(node.style?.textDecoration || node.style?.textDecorationLine || '').toLowerCase();
    return {
      bold: tag === 'strong' || tag === 'b' || fontWeight === 'bold' || Number.parseInt(fontWeight, 10) >= 600,
      italic: tag === 'em' || tag === 'i' || String(node.style?.fontStyle || '').toLowerCase() === 'italic',
      underline: tag === 'u' || textDecoration.includes('underline')
    };
  };

  const wrapStyledHtml = (html, style) => {
    let next = html;
    if (style.underline) next = `<u>${next}</u>`;
    if (style.italic) next = `<em>${next}</em>`;
    if (style.bold) next = `<strong>${next}</strong>`;
    return next;
  };

  const serializeInlineNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.nodeValue || '');
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const tag = node.tagName.toLowerCase();
    if (tag === 'br') return '<br>';
    if (isBlockTag(tag)) return serializeBlockNode(node);

    const childrenHtml = Array.from(node.childNodes)
      .map((child) => serializeInlineNode(child))
      .join('');

    return wrapStyledHtml(childrenHtml, readNodeStyle(node));
  };

  const serializeBlockNode = (node) => {
    const content = Array.from(node.childNodes)
      .map((child) => serializeInlineNode(child))
      .join('')
      .replace(/(?:<br>)+$/g, '');

    return content ? `<p>${content}</p>` : '<p></p>';
  };

  const topLevelParts = [];
  let inlineBuffer = '';
  let pendingTopLevelBreaks = 0;

  const flushPendingTopLevelBreaks = () => {
    if (pendingTopLevelBreaks <= 0) return;
    inlineBuffer += pendingTopLevelBreaks === 1 ? '<br>' : '<br><br>';
    pendingTopLevelBreaks = 0;
  };

  const flushInlineBuffer = () => {
    flushPendingTopLevelBreaks();
    if (!inlineBuffer && pendingTopLevelBreaks === 0) return;

    if (!inlineBuffer) {
      topLevelParts.push('<p></p>');
      pendingTopLevelBreaks = 0;
      return;
    }

    const paragraphs = inlineBuffer.split(/(?:<br>){2,}/).map((part) => part.replace(/(?:<br>)+$/g, ''));
    paragraphs.forEach((paragraph) => {
      topLevelParts.push(paragraph ? `<p>${paragraph}</p>` : '<p></p>');
    });
    inlineBuffer = '';
    pendingTopLevelBreaks = 0;
  };

  container.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && isBlockTag(node.tagName.toLowerCase())) {
      flushInlineBuffer();
      topLevelParts.push(serializeBlockNode(node));
      return;
    }

    const serialized = serializeInlineNode(node);
    if (serialized === '<br>') {
      pendingTopLevelBreaks += 1;
      return;
    }

    flushPendingTopLevelBreaks();
    inlineBuffer += serialized;
  });

  flushInlineBuffer();

  // Keep trailing empty paragraphs — they are intentional spacing that must
  // survive into the PDF. Only a value made up solely of empty paragraphs
  // collapses to an empty field.
  const html = topLevelParts.join('').trim();
  return /^(?:<p><\/p>)*$/.test(html) ? '' : html;
}

function RichTextField({ label, labelAction = null, value, onChange, disabled = false, minHeight = 42 }) {
  const editorRef = useRef(null);
  const lastHtmlRef = useRef('');

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || document.activeElement === editor) return;
    const nextHtml = valueToEditorHtml(value);
    if (lastHtmlRef.current !== nextHtml) {
      editor.innerHTML = nextHtml;
      lastHtmlRef.current = nextHtml;
    }
  }, [value]);

  const runCommand = (command) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, null);
    const nextHtml = normalizeEditorHtml(editorRef.current?.innerHTML || '');
    lastHtmlRef.current = nextHtml;
    onChange(nextHtml);
  };

  const handleInput = () => {
    const nextHtml = normalizeEditorHtml(editorRef.current?.innerHTML || '');
    lastHtmlRef.current = nextHtml;
    onChange(nextHtml);
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    document.execCommand('insertText', false, text);
    handleInput();
  };

  const insertPageBreak = () => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand('insertText', false, '\n[PAGEBREAK]\n');
    handleInput();
  };

  return (
    <div>
      <InlineStack align="space-between" blockAlign="center">
        <Text as="p" variant="bodyMd">
          {label}
        </Text>
        {labelAction}
      </InlineStack>
      <div style={{ border: '1px solid #c9cccf', borderRadius: '6px', overflow: 'hidden', marginTop: '4px', background: disabled ? '#f6f6f7' : '#fff' }}>
        <div style={{ display: 'flex', gap: '4px', padding: '6px', borderBottom: '1px solid #dde0e4', background: '#fafafa' }}>
          <button type="button" title="Bold" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => runCommand('bold')} style={{ minWidth: '28px', fontWeight: 700 }}>
            B
          </button>
          <button
            type="button"
            title="Italic"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => runCommand('italic')}
            style={{ minWidth: '28px', fontStyle: 'italic' }}
          >
            I
          </button>
          <button
            type="button"
            title="Underline"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => runCommand('underline')}
            style={{ minWidth: '28px', textDecoration: 'underline' }}
          >
            U
          </button>
          <button
            type="button"
            title="Insert a page break: puts [PAGEBREAK] on its own line — the PDF starts a new page there"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={insertPageBreak}
            style={{ marginLeft: 'auto', fontSize: '11px' }}
          >
            Page break
          </button>
        </div>
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          style={{ minHeight, padding: '8px 10px', outline: 'none', whiteSpace: 'pre-wrap', fontSize: '13px', lineHeight: 1.45 }}
        />
      </div>
    </div>
  );
}
const PDF_BRAND_OPTIONS = [
  { label: 'Roastworks', value: 'ROASTWORKS' },
  { label: 'Roasthubs', value: 'ROASTHUBS' },
  { label: 'Schuilenburg', value: 'SCHUILENBURG' },
  { label: 'Perfect Moose', value: 'PERFECT_MOOSE' }
];
const PDF_BRAND_VALUES = new Set(PDF_BRAND_OPTIONS.map((option) => option.value));
const LARGE_EMAIL_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function formatFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size >= 10 || unitIndex === 0 ? size.toFixed(0) : size.toFixed(1)} ${units[unitIndex]}`;
}

function normalizePdfBrand(value, fallback = 'ROASTWORKS') {
  const normalized = String(value || '')
    .trim()
    .toUpperCase();
  return PDF_BRAND_VALUES.has(normalized) ? normalized : fallback;
}

function normalizeMetaBrand(value) {
  const normalized = String(value || '')
    .trim()
    .toUpperCase();
  return PDF_BRAND_VALUES.has(normalized) ? normalized : '';
}

function getUserId(user) {
  return user?.GebruikerID ?? user?.gebruikerid ?? user?.UserID ?? user?.userId ?? user?.ID ?? user?.id ?? null;
}

function getUserName(user) {
  return user?.Gebruikernaam || user?.gebruikernaam || user?.Gebruiker || user?.gebruiker || user?.Name || user?.name || user?.Email || user?.email || '';
}

function getUserEmail(user) {
  return user?.Email || user?.email || '';
}

function normalizeMetaResponsibleSalesId(value) {
  if (value === undefined || value === null || value === '') return '';
  const parsed = Number.parseInt(String(value), 10);
  return Number.isInteger(parsed) && parsed > 0 ? String(parsed) : '';
}

function normalizePriceDisplayMode(value, fallback = 1) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 2 ? parsed : fallback;
}

export default function PdfExportControls({ quotationId, quotation, onQuotationRefresh }) {
  const previewHeight = 'clamp(900px, 88vh, 1400px)';
  const storeUsers = useLookupStore((state) => state.users);
  const setUsers = useLookupStore((state) => state.setUsers);
  const [localUsers, setLocalUsers] = useState([]);
  const [hasRequestedUsers, setHasRequestedUsers] = useState(false);
  const [displayMode, setDisplayMode] = useState(String(normalizePriceDisplayMode(quotation?.PriceDisplayMode, 1)));
  const [language, setLanguage] = useState(quotation?.PdfLanguage || 'E');
  const [brand, setBrand] = useState(normalizePdfBrand(quotation?.PdfBrand, 'ROASTWORKS'));
  const [includeOptional, setIncludeOptional] = useState(quotation?.PdfIncludeOptional !== false);
  const [includeSummary, setIncludeSummary] = useState(quotation?.PdfIncludeSummary !== false);
  const [includeTechnicalDescription, setIncludeTechnicalDescription] = useState(quotation?.PdfIncludeTechnicalDescription !== false);
  const [responsibleSalesUserId, setResponsibleSalesUserId] = useState(quotation?.ResponsibleSalesUserID ? String(quotation.ResponsibleSalesUserID) : '');
  const [responsibleSalesQuery, setResponsibleSalesQuery] = useState('');
  const [introductionText, setIntroductionText] = useState(quotation?.PdfIntroduction || '');
  const [commercialTerms, setCommercialTerms] = useState(() => normalizeCommercialTerms(quotation?.CommercialTerms, quotation?.PdfLanguage || 'E'));
  const [generating, setGenerating] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);
  const [openingStored, setOpeningStored] = useState(false);
  const [storedMeta, setStoredMeta] = useState(null);
  const [loadingStoredMeta, setLoadingStoredMeta] = useState(false);
  const [storedMetaError, setStoredMetaError] = useState('');
  const [previewNonce, setPreviewNonce] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editorTab, setEditorTab] = useState(0);
  const [hydratedQuotationId, setHydratedQuotationId] = useState(null);
  const metaBrand = useMemo(() => normalizeMetaBrand(storedMeta?.metadata?.brand), [storedMeta?.metadata?.brand]);
  const metaResponsibleSalesUserId = useMemo(() => normalizeMetaResponsibleSalesId(storedMeta?.metadata?.responsiblesalesuserid), [storedMeta?.metadata?.responsiblesalesuserid]);
  const users = useMemo(() => (Array.isArray(storeUsers) && storeUsers.length > 0 ? storeUsers : localUsers), [storeUsers, localUsers]);

  const loadStoredMeta = useCallback(async () => {
    if (!quotationId) return;
    setLoadingStoredMeta(true);
    setStoredMetaError('');
    try {
      const response = await fetch(`/api/project-quotations/${quotationId}/pdf/stored/meta`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || response.statusText);
      }
      const data = await response.json();
      setStoredMeta(data);
    } catch (err) {
      setStoredMeta(null);
      setStoredMetaError(err.message || 'Failed to fetch stored snapshot status');
    } finally {
      setLoadingStoredMeta(false);
    }
  }, [quotationId]);

  useEffect(() => {
    if (!quotationId || !quotation || Number(quotation.ID) !== Number(quotationId)) {
      setHydratedQuotationId(null);
      return;
    }
    const preferredBrand = normalizePdfBrand(quotation?.PdfBrand || metaBrand, 'ROASTWORKS');
    setDisplayMode(String(normalizePriceDisplayMode(quotation?.PriceDisplayMode, 1)));
    setLanguage(quotation?.PdfLanguage || 'E');
    setBrand(preferredBrand);
    setIncludeOptional(quotation?.PdfIncludeOptional !== false);
    setIncludeSummary(quotation?.PdfIncludeSummary !== false);
    setIncludeTechnicalDescription(quotation?.PdfIncludeTechnicalDescription !== false);
    setResponsibleSalesUserId(quotation?.ResponsibleSalesUserID ? String(quotation.ResponsibleSalesUserID) : '');
    setIntroductionText(quotation?.PdfIntroduction || '');
    setCommercialTerms(normalizeCommercialTerms(quotation?.CommercialTerms, quotation?.PdfLanguage || 'E'));
    setHydratedQuotationId(Number(quotationId));
  }, [
    quotation?.ID,
    quotation?.PriceDisplayMode,
    quotation?.PdfLanguage,
    quotation?.PdfBrand,
    quotation?.PdfIncludeOptional,
    quotation?.PdfIncludeSummary,
    quotation?.PdfIncludeTechnicalDescription,
    quotation?.ResponsibleSalesUserID,
    quotation?.PdfIntroduction,
    quotation?.CommercialTerms,
    metaBrand,
    hydratedQuotationId,
    quotationId
  ]);

  useEffect(() => {
    if (!quotationId || !quotation || Number(quotation.ID) !== Number(quotationId)) return;

    const quotationResponsibleMissing = quotation?.ResponsibleSalesUserID === undefined || quotation?.ResponsibleSalesUserID === null || String(quotation.ResponsibleSalesUserID).trim() === '';
    if (quotationResponsibleMissing && metaResponsibleSalesUserId && !responsibleSalesUserId) {
      setResponsibleSalesUserId(metaResponsibleSalesUserId);
    }
  }, [quotationId, quotation?.ID, quotation?.ResponsibleSalesUserID, metaResponsibleSalesUserId, responsibleSalesUserId]);

  useEffect(() => {
    if (Array.isArray(storeUsers) && storeUsers.length > 0) return;
    if (hasRequestedUsers) return;

    let cancelled = false;
    setHasRequestedUsers(true);
    (async () => {
      try {
        if (typeof setUsers === 'function') {
          await setUsers();
          return;
        }
        const fetchedUsers = await getRequest('/api/users');
        if (!cancelled && Array.isArray(fetchedUsers)) setLocalUsers(fetchedUsers);
      } catch (err) {
        console.log('Error loading responsible sales users:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storeUsers, setUsers, hasRequestedUsers]);

  const responsibleSalesOptions = useMemo(() => {
    const selectableUsers = Array.isArray(users) ? users.filter((user) => getUserId(user) != null) : [];
    const activeUsers = selectableUsers.filter((user) => !isInactiveUser(user)).sort((a, b) => getUserName(a).localeCompare(getUserName(b)));
    const selectedInactiveUser = responsibleSalesUserId ? selectableUsers.find((user) => String(getUserId(user)) === responsibleSalesUserId && isInactiveUser(user)) : null;
    const sortedUsers = selectedInactiveUser ? [selectedInactiveUser, ...activeUsers] : activeUsers;
    const formatLabel = (user) => `${getUserEmail(user) ? `${getUserName(user)} (${getUserEmail(user)})` : getUserName(user)}${isInactiveUser(user) ? ' - inactive' : ''}`;

    return [
      { label: 'No responsible sales person', value: '' },
      ...sortedUsers.map((user) => ({
        value: String(getUserId(user)),
        label: formatLabel(user)
      }))
    ];
  }, [users, responsibleSalesUserId]);
  const selectedResponsibleSalesLabel = useMemo(() => {
    const selected = responsibleSalesOptions.find((option) => option.value === responsibleSalesUserId);
    if (selected) return selected.label;
    if (responsibleSalesUserId) return 'Selected user is inactive';
    return '';
  }, [responsibleSalesOptions, responsibleSalesUserId]);
  const filteredResponsibleSalesOptions = useMemo(() => {
    const query = responsibleSalesQuery.trim().toLowerCase();
    if (!query) return responsibleSalesOptions;
    return responsibleSalesOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [responsibleSalesOptions, responsibleSalesQuery]);

  useEffect(() => {
    setResponsibleSalesQuery(selectedResponsibleSalesLabel);
  }, [selectedResponsibleSalesLabel]);

  const handleResponsibleSalesSelect = useCallback(
    (selected) => {
      const value = Array.isArray(selected) ? selected[0] : selected;
      const nextValue = value != null ? String(value) : '';
      setResponsibleSalesUserId(nextValue);
      const selectedOption = responsibleSalesOptions.find((option) => option.value === nextValue);
      setResponsibleSalesQuery(selectedOption?.label || '');
    },
    [responsibleSalesOptions]
  );
  const handleResponsibleSalesFieldFocus = useCallback(() => {
    setResponsibleSalesQuery('');
  }, []);
  const handleResponsibleSalesFieldBlur = useCallback(() => {
    if (!responsibleSalesQuery.trim()) {
      setResponsibleSalesQuery(selectedResponsibleSalesLabel);
    }
  }, [responsibleSalesQuery, selectedResponsibleSalesLabel]);

  const termsDirty = useMemo(
    () => !termsEqual(commercialTerms, quotation?.CommercialTerms, language) || String(introductionText || '') !== String(quotation?.PdfIntroduction || ''),
    [commercialTerms, quotation?.CommercialTerms, introductionText, quotation?.PdfIntroduction, language]
  );
  const snapshotSettingsDirty = useMemo(() => {
    const baselineBrand = normalizePdfBrand(quotation?.PdfBrand || metaBrand, 'ROASTWORKS');
    const baselineResponsibleSalesUserId = quotation?.ResponsibleSalesUserID ? String(quotation.ResponsibleSalesUserID) : metaResponsibleSalesUserId;

    return (
      displayMode !== String(normalizePriceDisplayMode(quotation?.PriceDisplayMode, 1)) ||
      language !== (quotation?.PdfLanguage || 'E') ||
      brand !== baselineBrand ||
      includeOptional !== (quotation?.PdfIncludeOptional !== false) ||
      includeSummary !== (quotation?.PdfIncludeSummary !== false) ||
      includeTechnicalDescription !== (quotation?.PdfIncludeTechnicalDescription !== false) ||
      responsibleSalesUserId !== baselineResponsibleSalesUserId
    );
  }, [
    displayMode,
    language,
    brand,
    includeOptional,
    includeSummary,
    includeTechnicalDescription,
    responsibleSalesUserId,
    metaBrand,
    metaResponsibleSalesUserId,
    quotation?.PriceDisplayMode,
    quotation?.PdfLanguage,
    quotation?.PdfBrand,
    quotation?.PdfIncludeOptional,
    quotation?.PdfIncludeSummary,
    quotation?.PdfIncludeTechnicalDescription,
    quotation?.ResponsibleSalesUserID
  ]);

  const updatePaymentLine = (index, value) => {
    setCommercialTerms((prev) => ({
      ...prev,
      paymentLines: prev.paymentLines.map((line, idx) => (idx === index ? value : line))
    }));
  };

  const updateTerm = (field, value) => {
    setCommercialTerms((prev) => ({ ...prev, [field]: value }));
  };

  const updateAttachmentLines = (value) => {
    setCommercialTerms((prev) => ({
      ...prev,
      attachmentLines: value.split(/\r?\n/)
    }));
  };

  useEffect(() => {
    loadStoredMeta();
  }, [loadStoredMeta]);

  useEffect(() => {
    if (!quotationId || !snapshotSettingsDirty) return;
    if (!quotation || Number(quotation.ID) !== Number(quotationId)) return;
    if (Number(hydratedQuotationId) !== Number(quotationId)) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`/api/project-quotations/${quotationId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            PriceDisplayMode: parseInt(displayMode, 10),
            PdfLanguage: language,
            PdfBrand: brand,
            PdfIncludeOptional: includeOptional,
            PdfIncludeSummary: includeSummary,
            PdfIncludeTechnicalDescription: includeTechnicalDescription,
            ResponsibleSalesUserID: responsibleSalesUserId ? parseInt(responsibleSalesUserId, 10) : null
          })
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || response.statusText);
        }
        if (!cancelled && onQuotationRefresh) {
          await onQuotationRefresh();
        }
      } catch (err) {
        console.log('Error persisting snapshot settings:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    quotationId,
    quotation?.ID,
    hydratedQuotationId,
    snapshotSettingsDirty,
    displayMode,
    language,
    brand,
    includeOptional,
    includeSummary,
    includeTechnicalDescription,
    responsibleSalesUserId,
    onQuotationRefresh
  ]);

  const persistTerms = useCallback(
    async (nextCommercialTerms = commercialTerms, nextIntroductionText = introductionText, languageOverride = null) => {
      if (!quotationId) return false;
      const effectiveLanguage = languageOverride || language;
      const normalizedTerms = normalizeCommercialTerms(nextCommercialTerms, effectiveLanguage);
      const normalizedIntroduction = String(nextIntroductionText || '');

      setSavingTerms(true);
      try {
        const response = await fetch(`/api/project-quotations/${quotationId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            CommercialTerms: normalizedTerms,
            PdfIntroduction: normalizedIntroduction,
            PriceDisplayMode: parseInt(displayMode, 10),
            PdfLanguage: effectiveLanguage,
            PdfBrand: brand,
            PdfIncludeOptional: includeOptional,
            PdfIncludeSummary: includeSummary,
            PdfIncludeTechnicalDescription: includeTechnicalDescription,
            ResponsibleSalesUserID: responsibleSalesUserId ? parseInt(responsibleSalesUserId, 10) : null
          })
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || response.statusText);
        }

        if (onQuotationRefresh) await onQuotationRefresh();
        return true;
      } catch (err) {
        console.log('Error saving commercial terms:', err);
        alert(`Saving terms failed: ${err.message}`);
        return false;
      } finally {
        setSavingTerms(false);
      }
    },
    [quotationId, commercialTerms, introductionText, displayMode, language, brand, includeOptional, includeSummary, includeTechnicalDescription, responsibleSalesUserId, onQuotationRefresh]
  );

  const handleSaveTerms = async () => {
    if (!quotationId) return;
    await persistTerms();
  };

  // Changing the language also swaps any block that still holds a default text
  // (in whichever language) for that block's default in the new language.
  const handleLanguageChange = useCallback(
    async (value) => {
      const nextLanguage = value;
      const nextTerms = translateTermsForLanguage(commercialTerms, nextLanguage);
      setLanguage(nextLanguage);
      setCommercialTerms(nextTerms);
      await persistTerms(nextTerms, introductionText, nextLanguage);
    },
    [commercialTerms, introductionText, persistTerms]
  );

  const handleAdditionalServicesEnabledChange = useCallback(
    async (value) => {
      const nextTerms = {
        ...commercialTerms,
        additionalServicesEnabled: value
      };
      setCommercialTerms(nextTerms);
      await persistTerms(nextTerms, introductionText);
    },
    [commercialTerms, introductionText, persistTerms]
  );

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`/api/project-quotations/${quotationId}/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceDisplayMode: parseInt(displayMode, 10),
          includeOptional,
          includeSummary,
          includeTechnicalDescription,
          brand,
          responsibleSalesUserId: responsibleSalesUserId ? parseInt(responsibleSalesUserId, 10) : null,
          pdfIntroduction: introductionText,
          language,
          commercialTerms
        })
      });

      if (response.ok) {
        await response.json().catch(() => ({}));
        if (onQuotationRefresh) await onQuotationRefresh();
        setPreviewNonce((v) => v + 1);
        loadStoredMeta();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`PDF generation failed: ${err.message || response.statusText}`);
      }
    } catch (err) {
      console.log('Error generating PDF:', err);
      alert(`PDF generation error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenStored = async () => {
    setOpeningStored(true);
    try {
      const response = await fetch(`/api/project-quotations/${quotationId}/pdf/stored`, {
        method: 'GET'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        const err = await response.json().catch(() => ({}));
        if (response.status === 404) {
          alert('No stored PDF snapshot exists yet for this version. Generate one first.');
        } else {
          alert(`Opening stored PDF failed: ${err.message || response.statusText}`);
        }
      }
    } catch (err) {
      console.log('Error opening stored PDF:', err);
      alert(`Opening stored PDF error: ${err.message}`);
    } finally {
      setOpeningStored(false);
    }
  };

  const hasStoredSnapshot = !!storedMeta?.exists;
  const generatedAt = storedMeta?.metadata?.generatedat || storedMeta?.lastModified;
  const storedSizeLabel = formatFileSize(storedMeta?.size);
  const storedSizeIsLarge = Number(storedMeta?.size || 0) >= LARGE_EMAIL_ATTACHMENT_BYTES;
  const previewUrl = useMemo(() => {
    if (!hasStoredSnapshot) return null;
    return `/api/project-quotations/${quotationId}/pdf/stored?preview=${previewNonce}`;
  }, [hasStoredSnapshot, quotationId, previewNonce]);
  const previewBusy = generating || previewLoading;
  const previewBusyLabel = generating ? 'Generating and compressing PDF...' : 'Loading PDF preview...';

  useEffect(() => {
    if (previewUrl) {
      setPreviewLoading(true);
    } else {
      setPreviewLoading(false);
    }
  }, [previewUrl]);

  return (
    <Card>
      <Text variant="headingSm" as="h3">
        PDF
      </Text>
      <div style={{ marginTop: '12px' }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch', flexWrap: 'wrap' }}>
          <div
            style={{
              flex: '1 1 640px',
              minWidth: '320px',
              minHeight: previewHeight,
              height: previewHeight,
              border: '1px solid #e1e3e5',
              borderRadius: '8px',
              overflow: 'hidden',
              background: '#fff',
              position: 'relative'
            }}
          >
            {loadingStoredMeta ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spinner size="small" />
              </div>
            ) : storedMetaError ? (
              <div style={{ padding: '16px' }}>
                <Text as="p" tone="critical">
                  {storedMetaError}
                </Text>
              </div>
            ) : hasStoredSnapshot ? (
              <>
                <iframe key={previewUrl} title="Stored quotation PDF preview" src={previewUrl} onLoad={() => setPreviewLoading(false)} style={{ width: '100%', height: previewHeight, border: '0' }} />
                {previewBusy && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255, 255, 255, 0.82)'
                    }}
                  >
                    <BlockStack gap="200" inlineAlign="center">
                      <Spinner size="small" />
                      <Text as="p" tone="subdued">
                        {previewBusyLabel}
                      </Text>
                    </BlockStack>
                  </div>
                )}
              </>
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                {generating ? (
                  <BlockStack gap="200" inlineAlign="center">
                    <Spinner size="small" />
                    <Text as="p" tone="subdued">
                      Generating and compressing PDF...
                    </Text>
                  </BlockStack>
                ) : (
                  <Text as="p" tone="subdued">
                    No stored snapshot yet. Generate a PDF to create and preview it here.
                  </Text>
                )}
              </div>
            )}
          </div>

          <div
            style={{
              flex: '1 1 500px',
              maxWidth: '560px',
              width: '100%',
              maxHeight: previewHeight,
              overflowY: 'auto',
              border: '1px solid #e1e3e5',
              borderRadius: '8px',
              padding: '12px',
              background: '#f6f6f7'
            }}
          >
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="span" variant="bodyMd">
                  Snapshot
                </Text>
                <Badge tone={hasStoredSnapshot ? 'success' : 'attention'}>{hasStoredSnapshot ? 'Stored' : 'Missing'}</Badge>
              </InlineStack>

              {generatedAt && (
                <Text as="p" tone="subdued">
                  Updated: {new Date(generatedAt).toLocaleString()}
                </Text>
              )}
              {storedSizeLabel && (
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="span" tone="subdued">
                    File size: {storedSizeLabel}
                  </Text>
                  {storedSizeIsLarge && <Badge tone="attention">Large for email</Badge>}
                </InlineStack>
              )}

              <InlineStack gap="200">
                <Button onClick={loadStoredMeta} loading={loadingStoredMeta} size="slim">
                  Refresh status
                </Button>
                <Button onClick={handleOpenStored} loading={openingStored} disabled={!hasStoredSnapshot}>
                  Open in new tab
                </Button>
              </InlineStack>

              <div style={{ borderTop: '1px solid #d2d5d8', paddingTop: '12px' }}>
                <BlockStack gap="200">
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 220px' }}>
                      <Select
                        label="Price display"
                        options={[
                          { label: 'All prices', value: '0' },
                          { label: 'Area totals only', value: '1' },
                          { label: 'Line totals only', value: '2' }
                        ]}
                        value={displayMode}
                        onChange={setDisplayMode}
                      />
                    </div>
                    <div style={{ flex: '1 1 180px' }}>
                      <Select
                        label="Language"
                        options={[
                          { label: 'English', value: 'E' },
                          { label: 'Nederlands', value: 'N' },
                          { label: 'Francais', value: 'F' }
                        ]}
                        value={language}
                        onChange={handleLanguageChange}
                      />
                    </div>
                    <div style={{ flex: '1 1 200px' }}>
                      <Select label="Brand" options={PDF_BRAND_OPTIONS} value={brand} onChange={(value) => setBrand(normalizePdfBrand(value, brand))} />
                    </div>
                  </div>
                  <div>
                    <Combobox
                      activator={
                        <Combobox.TextField
                          label="Responsible sales person"
                          value={responsibleSalesQuery}
                          onChange={setResponsibleSalesQuery}
                          onFocus={handleResponsibleSalesFieldFocus}
                          onBlur={handleResponsibleSalesFieldBlur}
                          placeholder="Search active users"
                          autoComplete="off"
                        />
                      }
                    >
                      <Listbox onSelect={handleResponsibleSalesSelect}>
                        {filteredResponsibleSalesOptions.map((option) => (
                          <Listbox.Option key={option.value || '__none'} value={option.value} selected={responsibleSalesUserId === option.value}>
                            {option.label}
                          </Listbox.Option>
                        ))}
                      </Listbox>
                    </Combobox>
                    {filteredResponsibleSalesOptions.length === 0 && (
                      <div style={{ marginTop: '4px' }}>
                        <Text as="p" tone="subdued">
                          No active users found for this search.
                        </Text>
                      </div>
                    )}
                  </div>
                  <Checkbox label="Include optional" checked={includeOptional} onChange={setIncludeOptional} />
                  <Checkbox label="Include summary" checked={includeSummary} onChange={setIncludeSummary} />
                  <Checkbox label="Include technical descriptions" checked={includeTechnicalDescription} onChange={setIncludeTechnicalDescription} />

                  <div style={{ borderTop: '1px solid #d2d5d8', paddingTop: '12px' }}>
                    <BlockStack gap="200">
                      <InlineStack align="space-between" blockAlign="center">
                        <Text as="span" variant="bodyMd">
                          Snapshot terms
                        </Text>
                        {termsDirty && <Badge tone="attention">Unsaved</Badge>}
                      </InlineStack>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Press Enter for a new line. To force a page break in the PDF, put [PAGEBREAK] on its own line — or use the "Page break" button in any text field.
                      </Text>
                      <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #e1e3e5' }}>
                        {SNAPSHOT_EDITOR_TABS.map((tab, idx) => (
                          <div
                            key={tab.id}
                            onClick={() => setEditorTab(idx)}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: editorTab === idx ? '2px solid #000' : '2px solid transparent',
                              marginBottom: '-1px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              backgroundColor: editorTab === idx ? '#f6f6f7' : 'transparent',
                              borderRadius: '6px 6px 0 0',
                              transition: 'background-color 0.15s ease'
                            }}
                          >
                            <Text variant="bodySm" as="span" fontWeight={editorTab === idx ? 'semibold' : 'regular'}>
                              {tab.content}
                            </Text>
                          </div>
                        ))}
                      </div>

                      <div style={{ marginTop: '12px' }}>
                        {editorTab === 0 ? (
                          <BlockStack gap="200">
                            <RichTextField
                              label="Introduction text (printed under quotation title)"
                              labelAction={<IntroductionWriter quotationId={quotationId} language={language} currentText={introductionText} onUse={setIntroductionText} />}
                              value={introductionText}
                              onChange={setIntroductionText}
                              minHeight={180}
                            />
                          </BlockStack>
                        ) : editorTab === 1 ? (
                          <BlockStack gap="200">
                            <RichTextField label="Payment line 1" value={commercialTerms.paymentLines[0]} onChange={(value) => updatePaymentLine(0, value)} />
                            <RichTextField label="Payment line 2" value={commercialTerms.paymentLines[1]} onChange={(value) => updatePaymentLine(1, value)} />
                            <RichTextField label="Payment line 3" value={commercialTerms.paymentLines[2]} onChange={(value) => updatePaymentLine(2, value)} minHeight={72} />
                            <RichTextField label="Warranty" value={commercialTerms.warranty} onChange={(value) => updateTerm('warranty', value)} />
                            <RichTextField label="Delivery time" value={commercialTerms.deliveryTime} onChange={(value) => updateTerm('deliveryTime', value)} />
                            <RichTextField label="Labor costs" value={commercialTerms.laborCosts} onChange={(value) => updateTerm('laborCosts', value)} minHeight={60} />
                            <RichTextField label="Quotation validity" value={commercialTerms.quotationValidity} onChange={(value) => updateTerm('quotationValidity', value)} />
                            <TextField label="Conditions title" value={commercialTerms.conditionsTitle} onChange={(value) => updateTerm('conditionsTitle', value)} autoComplete="off" />
                            <RichTextField label="Conditions" value={commercialTerms.conditionsText} onChange={(value) => updateTerm('conditionsText', value)} minHeight={90} />
                            <TextField label="Design block title" value={commercialTerms.designScopeTitle} onChange={(value) => updateTerm('designScopeTitle', value)} autoComplete="off" />
                            <RichTextField
                              label="Design basis, scope and exclusions"
                              value={commercialTerms.designScopeText}
                              onChange={(value) => updateTerm('designScopeText', value)}
                              minHeight={260}
                            />
                          </BlockStack>
                        ) : (
                          <BlockStack gap="200">
                            <Checkbox label="Additional services at a fixed price" checked={commercialTerms.additionalServicesEnabled} onChange={handleAdditionalServicesEnabledChange} />
                            <TextField
                              label="Additional services title"
                              value={commercialTerms.additionalServicesTitle}
                              onChange={(value) => updateTerm('additionalServicesTitle', value)}
                              autoComplete="off"
                              disabled={!commercialTerms.additionalServicesEnabled}
                            />
                            <RichTextField
                              label="Additional services text"
                              value={commercialTerms.additionalServicesText}
                              onChange={(value) => updateTerm('additionalServicesText', value)}
                              disabled={!commercialTerms.additionalServicesEnabled}
                              minHeight={300}
                            />
                            <RichTextField label="Closing paragraph 1" value={commercialTerms.closingIntro} onChange={(value) => updateTerm('closingIntro', value)} minHeight={90} />
                            <RichTextField label="Closing paragraph 2" value={commercialTerms.closingFollowUp} onChange={(value) => updateTerm('closingFollowUp', value)} minHeight={72} />
                            <RichTextField label="Signature block" value={commercialTerms.signatureBlock} onChange={(value) => updateTerm('signatureBlock', value)} minHeight={90} />
                            <TextField label="Attachment title" value={commercialTerms.attachmentsTitle} onChange={(value) => updateTerm('attachmentsTitle', value)} autoComplete="off" />
                            <TextField
                              label="Attachment lines (one per line)"
                              value={(commercialTerms.attachmentLines || []).join('\n')}
                              onChange={updateAttachmentLines}
                              autoComplete="off"
                              multiline={5}
                            />
                          </BlockStack>
                        )}
                      </div>

                      <div style={{ paddingTop: '4px' }}>
                        <Button onClick={handleSaveTerms} loading={savingTerms} disabled={!termsDirty}>
                          Save terms
                        </Button>
                      </div>
                    </BlockStack>
                  </div>

                  <Button variant="primary" onClick={handleGenerate} loading={generating}>
                    {hasStoredSnapshot ? 'Regenerate Snapshot' : 'Generate Snapshot'}
                  </Button>
                </BlockStack>
              </div>
            </BlockStack>
          </div>
        </div>
      </div>
    </Card>
  );
}
